import React, { useState, useEffect, useMemo } from 'react';
import FormControl from '@material-ui/core/FormControl';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import { KeyboardDatePicker } from '@material-ui/pickers';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AddIcon from '@material-ui/icons/Add';
import TransformerPrepaidReadingHelper, { ITransformerPrepaidReading } from '../../../../../../@types/model/transformer/reading/transformerPrepaidReading';
import GeneralFunctions from '../../../../../../store/general/functions';
import { IUserSession } from '../../../../../../@types/employee';
import { firestore } from 'firebase/app';
import { ITransformer } from '../../../../../../@types/model/transformer/transformer';
import StandardFab from '../../../../../customComponents/button/StandardFab';
import TransformerDropdown from '../../../../../customComponents/selector/transformer/TransformerSelector';
import { DATEPICKER_FORMAT_DEFAULT } from '../../../../../../appConstants';
import { FormHelperText, InputAdornment, Tooltip, IconButton, Icon, Button, Typography } from '@material-ui/core';
import uuid from 'uuid';
import firebaseApp, { FirebaseService } from '../../../../../../services/firebaseService';
import { EnumFileUploadQueueType } from '../../../../../../@types/model/files/enum';
import Dropzone from 'react-dropzone';
import ImageEmbedView from '../../../../../customComponents/embed/ImageEmbedView';
import FileEmbedView from '../../../../../customComponents/embed/FileEmbedView';
import { IRootState } from '../../../../../../@types/redux';
import { connect } from 'react-redux';
import { IValueAddedTax } from '../../../../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import MasterDataValueAddedTaxFunctions from '../../../../../../store/masterData/valueAddedTax/functions';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';

interface IEditTransformerPrepaidReadingDialogProps {
    reading ?: ITransformerPrepaidReading;
    valueAddedTaxes : Array<IValueAddedTax>; 

    disabled ?: boolean;

    session ?: IUserSession | null;
}

const TransformerPrepaidReadingDialogComponent = (props : IEditTransformerPrepaidReadingDialogProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [date, setDate] = useState<moment.Moment | null>(null);
    const [electricityAmount, setElectricityAmount] = useState<number>(0);
    const [electricityUnitAmount, setElectricityUnitAmount] = useState<number>(0);
    const [waterAmount, setWaterAmount] = useState<number>(0);
    const [comment, setComment] = useState<string>('');
    const [division, setDivision] = useState<string>('');
    const [subArea, setSubArea] = useState<string>('');
    const [group, setGroup] = useState<string>('');
    const [transformerNumber, setTransformerNumber] = useState<string>('');
    const [imageFile, setImageFile] = useState<File | null>(null);

    useEffect(() => {
        if (props.reading) {
            setDate(!props.reading.date ? moment().utc().startOf('day') : moment.utc(props.reading.date));
            setElectricityAmount(!props.reading.electricityTotal ? 0 : props.reading.electricityTotal);
            setElectricityUnitAmount(!props.reading.electricityUnits ? 0 : props.reading.electricityUnits);
            setWaterAmount(!props.reading.waterTotal ? 0 : props.reading.waterTotal);
            setComment(!props.reading.comment ? '' : props.reading.comment);
            setTransformerNumber(!props.reading.transformerRef ? '' : props.reading.transformerRef);
            setDivision(!props.reading.division ? '' : props.reading.division);
            setSubArea(!props.reading.subArea ? '' : props.reading.subArea);
            setGroup(!props.reading.group ? '' : props.reading.group);
        } else {
            setDate(null);
            setElectricityAmount(0);
            setElectricityUnitAmount(0);
            setWaterAmount(0);
            setComment('');
            setTransformerNumber('');
            setDivision('');
            setSubArea('');
            setGroup('');
            setImageFile(null);
        }
    }, [props.reading]);

    useEffect(() => {
        load();
    }, []);

    const load = (): void => {
        MasterDataValueAddedTaxFunctions.load();
    };

    const onElectricityAmountChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setElectricityAmount(Number(event.currentTarget.value));
    };

    const onElectricityUnitAmountChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setElectricityUnitAmount(Number(event.currentTarget.value));
    };

    const onWaterAmountChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setWaterAmount(Number(event.currentTarget.value));
    };
    
    const onCommentChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setComment(event.currentTarget.value);
    };

    const onDateChange = (newDate : moment.Moment | null) : void => {
        setDate(newDate?.asUTC() ?? null);
    };

    const save = async () : Promise<void> => {
        const { session, reading } = props;

        if (!session) return;

        setIsLoading(true);

        const ref = TransformerPrepaidReadingHelper.doc(reading?.id);

        try {
            await firebaseApp.firestore().runTransaction(async (transaction) => {
                if (!transformerNumber) throw new Error('Ref Required');

                const doc = await transaction.get(ref);
                const data = doc.data();
                const guid = reading?.guid ?? uuid.v4();

                TransformerPrepaidReadingHelper.save(transaction, {
                    ...data,
                    guid,
                    electricityTotal: electricityAmount,
                    electricityUnits: electricityUnitAmount,
                    waterTotal: waterAmount,
                    transformerRef: transformerNumber,
                    comment,
                    date: date?.valueOf() ?? 0,
                    group,
                    division,
                    subArea,
                    imageFileName: data?.imageFileName ?? imageFile?.name ?? '',
                    createdBy: data?.createdBy ?? session.firebaseUser.uid,
                    createdByName: data?.createdByName ?? session.employee.Name,
                    createdByEmployee: data?.createdByEmployee ?? session.employee.EmployeeNumber ?? '',
                    createdOn: data?.createdOn ?? firestore.Timestamp.now().toMillis(),
                    updatedBy: session.firebaseUser.uid,
                    updatedByName: session.employee.Name,
                    updatedByEmployee: session.employee.EmployeeNumber ?? '',
                    updatedOn: firestore.Timestamp.now().toMillis(),
                    isActive: data?.isActive ?? true,
                    isWeb: data?.isWeb ?? true,
                } as ITransformerPrepaidReading, props.valueAddedTaxes);
    
                if (imageFile) {
                    await TransformerPrepaidReadingHelper.uploadFile(
                        transformerNumber,
                        imageFile,
                        {
                            collection: TransformerPrepaidReadingHelper.COLLECTION_NAME,
                            fieldName: 'imageFileUrl',
                            fileType: EnumFileUploadQueueType.TransformerPrepaidReading.toString(),
                            refGuid: guid,
                            thumbnailFieldName: 'imageFileThumbnailUrl',
                        }
                    );
                }
            });

            setOpen(false);
        } catch (ex) {
            GeneralFunctions.generalShowErrorSnackbar(`An error while saving reading. ${ex}`);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = () : void => {
        save();
    };

    const onClose = () : void => {
        if (isLoading) return;

        setOpen(false);
    };

    const onClick = () : void => {
        setOpen(true);
    };

    const onTransformerChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber ?? '');
        setDivision(value?.Division ?? '');
        setSubArea(value?.SubArea ?? '');
        setGroup(value?.EPGroup ?? '');
    };

    const onFileDrop = (acceptedFiles : Array<File>) : void => {
        if (!acceptedFiles.length) return;

        setImageFile(new File([acceptedFiles[0].slice()], `${acceptedFiles[0].name}`, {
            type: acceptedFiles[0].type,
        }));
    };

    return (
        <>
            {
                props.reading &&
                <Tooltip title='Info'>
                    <div>
                        <IconButton color='primary' onClick={onClick}
                            aria-label='Info'>
                            {
                                !props.disabled ? (
                                    <Icon>edit</Icon>
                                ) : (
                                    <InfoOutlinedIcon />
                                )
                            }
                        </IconButton>
                    </div>
                </Tooltip>
            }
            {
                !props.reading &&
                <Tooltip title='Add'>
                    <div>
                        <StandardFab aria-label='add' disabled={isLoading} onClick={onClick}>
                            <AddIcon />
                        </StandardFab>
                    </div>
                </Tooltip>
            }
            <ProjectDialog
                title={props.reading ? 'Edit Prepaid Reading' : 'Create Prepaid Reading'}
                isOpen={open}
                isLoadingCircular={isLoading}
                onClose={onClose}
                maxWidth={'md'}
                fullWidth
            >
                <div className='fdc flx1 hfill p10'>
                    <DialogContent className='fdc flx1 hfill'>
                        <div className={'fdr flx1 aifs mt26'}>
                            <div className={'flx1 ais pr20'}>
                                <TransformerDropdown
                                    value={transformerNumber}
                                    label='Select Transformer'
                                    fullWidth
                                    required
                                    onChange={onTransformerChange}
                                    disabled={props.disabled}
                                    type='prepaid'
                                    autoFocus
                                />
                            </div>
                            <div className={'flx1 ais pr10'}>
                                <FormControl fullWidth>
                                    <KeyboardDatePicker
                                        disabled={props.disabled}
                                        autoComplete='off'
                                        id='date'
                                        label='Date'
                                        value={date}
                                        format={DATEPICKER_FORMAT_DEFAULT}
                                        onChange={onDateChange}
                                        required
                                        error={!date}
                                        helperText={DATEPICKER_FORMAT_DEFAULT}
                                    />
                                    {
                                        !date &&
                                        <FormHelperText error>Required</FormHelperText>
                                    }
                                </FormControl>
                            </div>
                            <div className={'flx1 ais pl10'}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled={props.disabled}
                                        autoComplete='off'
                                        id='electricityAmount'
                                        label='Electricity Amount'
                                        value={!electricityAmount ? '' : electricityAmount}
                                        onChange={onElectricityAmountChange}
                                        type={'number'}
                                        required
                                        error={!electricityAmount}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='end'>
                                                    R
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {
                                        !electricityAmount &&
                                        <FormHelperText error>Required</FormHelperText>
                                    }
                                </FormControl>
                            </div>
                        </div>
                        <div className={'fdr flx1 aifs mt26'}>
                            <div className={'flx1 ais pr20'}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled={props.disabled}
                                        autoComplete='off'
                                        id='electricityUnitAmount'
                                        label='Electricity Units'
                                        value={!electricityUnitAmount ? '' : electricityUnitAmount}
                                        onChange={onElectricityUnitAmountChange}
                                        type={'number'}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    kWh
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais pr10'}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled={props.disabled}
                                        autoComplete='off'
                                        id='waterAmount'
                                        label='Water Amount'
                                        value={!waterAmount ? '' : waterAmount}
                                        onChange={onWaterAmountChange}
                                        type={'number'}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='end'>
                                                    R
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                            </div>
                            <div className={'flx1 ais pl10'}>
                            </div>
                        </div>
                        <div className={'fdr flx1 aifs mt26'}>
                            <div className={'flx1 ais'}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled={props.disabled}
                                        autoComplete='off'
                                        id='comment'
                                        label='Comment'
                                        value={comment}
                                        onChange={onCommentChange}
                                        margin='normal'
                                        className={'TextField'}
                                    />
                                </FormControl>
                            </div>
                        </div>
                        {
                            !props.reading?.imageFileUrl &&
                            <div className='fdr h400 mt26'>
                                <Dropzone onDrop={onFileDrop} accept={'image/*,application/pdf'}>
                                    {({ getRootProps, getInputProps }) => (
                                        <section className='fdc flx1'>
                                            <div className='fdc flx1' {...getRootProps()}>
                                                <input disabled={isLoading} multiple={false} {...getInputProps()} />
                                                
                                                {
                                                    !imageFile &&
                                                    <div className='fdr flx1 jcc aic'>
                                                        <Typography className='cg0' variant='body2'>
                                                            DRAG & DROP FILE
                                                        </Typography>
                                                    </div>
                                                }
                                                
                                                {
                                                    imageFile?.type.includes('image') &&
                                                    <ImageEmbedView width='100%' file={imageFile} />
                                                }
                                                
                                                {
                                                    imageFile?.type.includes('pdf') &&
                                                    <FileEmbedView file={imageFile} />
                                                }
                                            </div>
                                        </section>
                                    )}
                                </Dropzone>
                            </div>
                        }
                    </DialogContent>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button disabled={isLoading || props.disabled} className={'mr20'} onClick={onSubmit} variant='contained' color='primary'>
                                OK
                        </Button>
                        <Button disabled={isLoading} variant='outlined' onClick={onClose} color='primary'>
                                CANCEL
                        </Button>
                    </div>
                </div>
            </ProjectDialog>
        </>);
};

const mapStateToProps = (state : IRootState) => {
    return {
        valueAddedTaxes: state.masterData.valueAddedTax.valueAddedTaxes,
    };
};
    
const EditTransformerPrepaidReadingDialog = connect(
    mapStateToProps,
)(TransformerPrepaidReadingDialogComponent);

export default EditTransformerPrepaidReadingDialog;
